import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

export default function BautagebuchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bericht, setBericht] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get(`/bautagebuecher/${id}`).then(r => {
      setBericht(r.data);
      setFotos(r.data.fotos || []);
    }).catch(() => navigate('/bautagebuch'));
  }, [id]);

  const handleFotos = async (files) => {
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('fotos', f);
      fd.append('bautagesbuch_id', id);
      const r = await api.post('/fotos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFotos(prev => [...prev, ...r.data]);
    } catch { alert('Upload fehlgeschlagen'); }
    finally { setUploading(false); }
  };

  const deleteFoto = async (fotoId) => {
    if (!window.confirm('Foto löschen?')) return;
    await api.delete(`/fotos/${fotoId}`).catch(() => {});
    setFotos(prev => prev.filter(f => f.id !== fotoId));
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    window.open(`/api/bautagebuecher/${id}/pdf?token=${token}`, '_blank');
  };

  if (!bericht) return <div className="loading-screen"><div className="spinner" /></div>;

  const b = bericht;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Bautagebuch #{id}</div>
          <div className="page-sub">{b.datum} · {b.baustelle_name || '–'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/bautagebuch')}>← Zurück</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/bautagebuch/${id}/bearbeiten`)}>✏️ Bearbeiten</button>
          <button className="btn btn-primary" onClick={exportPdf}>📄 Als PDF exportieren</button>
        </div>
      </div>

      {/* Kopfdaten */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title" style={{ marginBottom: 14 }}>Kopfdaten</div>
        <div className="stat-grid">
          {[['Datum', b.datum], ['Baustelle', b.baustelle_name||'–'], ['Bauleiter', b.bauleiter||'–'], ['Status', b.status]].map(([l,v]) => (
            <div className="stat-card" key={l}><div className="stat-label">{l}</div><div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{v}</div></div>
          ))}
        </div>
      </div>

      {/* Witterung */}
      {(b.temperatur || b.wetter || b.wind) && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 14 }}>Witterungsbedingungen</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[['Temperatur', b.temperatur], ['Wetter', b.wetter], ['Wind', b.wind], ['Niederschlag', b.niederschlag], ['Sichtweite', b.sichtweite], ['Besonderheiten', b.wetter_bem]].filter(([,v]) => v).map(([l,v]) => (
              <div key={l} style={{ background: 'var(--grey-bg)', padding: '10px 14px', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{l}</div>
                <div style={{ fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Arbeitszeit */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title" style={{ marginBottom: 14 }}>Personal & Arbeitszeit</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[['Beginn', b.arbeitsbeginn], ['Ende', b.arbeitsende], ['Std. gesamt', b.std_gesamt], ['Monteure', b.anzahl_monteure], ['Gesamtstunden MA', b.gesamtstunden]].map(([l,v]) => (
            <div key={l} style={{ background: 'var(--grey-bg)', padding: '10px 14px', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{l}</div>
              <div style={{ fontWeight: 600, color: 'var(--blue)' }}>{v || '–'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Material */}
      {bericht.material?.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 14 }}>Materialeinlage laut LV</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Pos.-Nr.</th><th>Bezeichnung</th><th>Einh.</th><th>Menge</th><th>Lage</th><th>Bemerkung</th></tr></thead>
              <tbody>
                {bericht.material.filter(m => m.menge || m.lage).map((m, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>{m.pos_nr}</td>
                    <td>{m.bezeichnung}</td>
                    <td style={{ textAlign: 'center' }}>{m.einheit}</td>
                    <td style={{ fontWeight: 600 }}>{m.menge}</td>
                    <td>{m.lage}</td>
                    <td>{m.bemerkung}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Leistungen */}
      {b.leistungen && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 10 }}>Leistungen des Tages</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{b.leistungen}</div>
        </div>
      )}

      {/* Fotos */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-head">
          <div className="card-title">Fotodokumentation ({fotos.length} Fotos)</div>
          <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
            {uploading ? '⏳ Lädt hoch…' : '📷 Fotos hinzufügen'}
            <input type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={async e => { if (!e.target.files.length) return; await handleFotos(Array.from(e.target.files)); e.target.value = ''; }} />
          </label>
        </div>
        {fotos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)', fontSize: 13 }}>
            📷 Noch keine Fotos — oben hinzufügen
          </div>
        ) : (
          <div className="photo-grid">
            {fotos.map(f => (
              <div key={f.id} className="photo-item" style={{ position: 'relative' }}>
                <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button onClick={() => deleteFoto(f.id)}
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unterschriften */}
      {(b.sig_ersteller || b.sig_bauleiter) && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 14 }}>Unterschriften</div>
          <div className="grid-2">
            {b.sig_ersteller && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 6 }}>DV Elektro (Ausführender)</div>
                <img src={b.sig_ersteller} alt="Unterschrift" style={{ width: '100%', height: 80, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'white' }} />
              </div>
            )}
            {b.sig_bauleiter && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 6 }}>Bauleiter / Polier (Auftraggeber)</div>
                <img src={b.sig_bauleiter} alt="Unterschrift" style={{ width: '100%', height: 80, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'white' }} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sonstiges */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {b.stillstand && <span className="badge badge-red">⚠️ Stillstand: {b.stillstand_grund}</span>}
          {b.foto_doku && <span className="badge badge-blue">📷 Fotodokumentation erstellt</span>}
          {b.anfahrt && <span className="badge badge-amber">🚗 Anfahrtspauschale 50€</span>}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 32 }}>
        <button className="btn btn-secondary" onClick={() => navigate(`/bautagebuch/${id}/bearbeiten`)}>✏️ Bearbeiten</button>
        <button className="btn btn-primary" style={{ padding: '10px 32px' }} onClick={exportPdf}>📄 Als PDF exportieren</button>
      </div>
    </div>
  );
}
